import { forwardRef } from 'react';
import { AnimatePresence, MotionConfig, motion, useReducedMotion } from 'framer-motion';
import { Box, Button, Modal as JoyModal, ModalDialog as JoyDialog, Typography } from '@mui/joy';
import useMediaQuery from '@mui/material/useMediaQuery';
import './workspace.css';

// Scope the design to these workspaces and their portal-based dialogs.
export function Workspace({ children, className = '', sx, ...props }) {
  const reduce = useReducedMotion();
  return (
    <MotionConfig reducedMotion="user">
      <Box
        component={motion.main}
        initial={{ opacity: 0, y: reduce ? 0 : 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: reduce ? 0 : 0.24 }}
        className={`studio-workspace studio ${className}`}
        sx={sx}
        {...props}
      >
        {children}
      </Box>
    </MotionConfig>
  );
}
export function WorkspaceHeader({ title, subtitle, children }) {
  return (
    <header className="studio-heading">
      <Box>
        <Typography component="h1" level="h2">
          {title}
        </Typography>
        {subtitle && (
          <Typography level="body-sm" sx={{ mt: 0.5, color: 'text.tertiary' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box className="studio-actions">{children}</Box>
    </header>
  );
}
export function SummaryCards({ items }) {
  return (
    <div className="studio-summary" style={{ '--summary-count': items.length }}>
      {items.map(({ label, value, hint, icon, tone = 'primary', onClick }) => (
        <Box
          key={label}
          component={onClick ? 'button' : 'div'}
          type={onClick ? 'button' : undefined}
          onClick={onClick}
          className={`studio-summary-card ${onClick ? 'is-action' : ''}`}
        >
          {icon && (
            <Box className="studio-icon" sx={{ bgcolor: `${tone}.softBg`, color: `${tone}.plainColor` }}>
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
              {label}
            </Typography>
            <Typography level="h3" component="p" className="studio-number">
              {value ?? '—'}
            </Typography>
            {hint && (
              <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                {hint}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </div>
  );
}
export function WorkspaceTabs({ value, onChange, items, label = 'Разделы' }) {
  return (
    <div className="studio-tabs" role="group" aria-label={label}>
      {items.map((item) => (
        <Button
          key={item.value}
          size="sm"
          variant="plain"
          color={value === item.value ? 'primary' : 'neutral'}
          aria-pressed={value === item.value}
          className={value === item.value ? 'is-active' : ''}
          onClick={() => onChange(item.value)}
          startDecorator={item.icon}
        >
          {item.label}
          {item.count != null && <span className="studio-count">{item.count}</span>}
        </Button>
      ))}
    </div>
  );
}
export function Reveal({ show, children }) {
  const reduce = useReducedMotion();
  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.div
          key="panel"
          initial={{ opacity: 0, height: reduce ? 'auto' : 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: reduce ? 'auto' : 0 }}
          transition={{ duration: reduce ? 0 : 0.18 }}
          style={{ overflow: 'hidden' }}
        >
          <Box sx={{ py: 0.5 }}>{children}</Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
// Keep the Joy focus trap and restore-focus lifecycle through the exit animation.
export const StudioModal = forwardRef(function StudioModal(
  { open, children, className = '', sx, ...props },
  ref,
) {
  const reduce = useReducedMotion();
  return (
    <MotionConfig reducedMotion="user">
      <AnimatePresence>
        {open && (
          <JoyModal
            {...props}
            sx={[{ zIndex: 10120 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
            ref={ref}
            open
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.16 }}
            className={`studio studio-modal ${className}`}
          >
            {children}
          </JoyModal>
        )}
      </AnimatePresence>
    </MotionConfig>
  );
});
export const StudioDialog = forwardRef(function StudioDialog({ className = '', sx, ...props }, ref) {
  const mobile = useMediaQuery('(max-width:700px), (max-width:1000px) and (max-height:500px)');
  const layout = mobile ? 'fullscreen' : props.layout || 'center';
  return (
    <JoyDialog
      {...props}
      layout={layout}
      data-studio-fullscreen={layout === 'fullscreen' ? 'true' : undefined}
      ref={ref}
      className={`studio-dialog ${className}`}
      sx={[
        {
          borderRadius: '18px',
          borderColor: 'divider',
          boxShadow: '0 24px 90px rgba(20,20,40,.16)',
          maxWidth: 'calc(100vw - 24px)',
          maxHeight: '92dvh',
          overflowY: 'auto',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
});
export const money = (value) =>
  value == null ? '—' : `${Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;
export const personName = (person) =>
  [person?.last_name, person?.name, person?.patronymic].filter(Boolean).join(' ') ||
  `Исполнитель №${person?.id ?? ''}`;
